import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ProductDetail, ScoreSnapshot } from '../types';

/**
 * T049: useProductScore hook.
 * Fetches GET /api/products/:id and GET /api/products/:id/score-history.
 */

export function useProductDetail(productId: string | undefined) {
  return useQuery<ProductDetail>({
    queryKey: ['product', productId],
    queryFn: () => api.get<ProductDetail>(`/products/${productId}`),
    enabled: !!productId,
    staleTime: 30_000,
  });
}

export function useScoreHistory(productId: string | undefined) {
  return useQuery<ScoreSnapshot[]>({
    queryKey: ['product', productId, 'score-history'],
    queryFn: () => api.get<ScoreSnapshot[]>(`/products/${productId}/score-history`),
    enabled: !!productId,
    staleTime: 60_000,
  });
}

/**
 * Mutation to recheck ACP + UCP readiness scores for a single product.
 * Calls POST /api/scan/product/:id/recheck-readiness, then invalidates
 * the product detail query so the UI refreshes with updated scores.
 */
export function useRecheckReadiness(productId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<{ acpScore: number; ucpScore: number }, Error>({
    mutationFn: () => api.post(`/scan/product/${productId}/recheck-readiness`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['product', productId] });
    },
  });
}
